import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  TextField,
  IconButton,
  Divider,
  Popover,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  ScreenWrap,
  HeaderBreadcrumbs,
  SoloLoading,
  ScreenError,
} from '../../../components';
import { Plus, PlusCircle, TrashSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { Api, useApiCallable } from '../../../api';
import { Model } from '../../../models';
import { toastErrorHandler } from '../../../util';
import { format } from 'date-fns';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
}

interface DaySchedule {
  day: string;
  label: string;
  slots: TimeSlot[];
}

interface TimeSlotBlockProps {
  slot: TimeSlot;
  onUpdateSlot: (field: 'startTime' | 'endTime', value: string) => void;
  onRemoveSlot: () => void;
}

function TimeSlotBlock({
  slot,
  onUpdateSlot,
  onRemoveSlot,
}: TimeSlotBlockProps) {
  return (
    <Box
      sx={{
        backgroundColor: 'background.level2',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <TextField
        type="time"
        value={slot.startTime}
        onChange={(e) => onUpdateSlot('startTime', e.target.value)}
        size="small"
        variant="outlined"
        sx={{ minWidth: 100, backgroundColor: 'white', borderRadius: 1 }}
        slotProps={{ htmlInput: { step: 900 } }}
      />
      <Typography variant="body2">-</Typography>
      <TextField
        type="time"
        value={slot.endTime}
        onChange={(e) => onUpdateSlot('endTime', e.target.value)}
        size="small"
        variant="outlined"
        sx={{ minWidth: 100, backgroundColor: 'white', borderRadius: 1 }}
        slotProps={{ htmlInput: { step: 900 } }}
      />
      <IconButton onClick={onRemoveSlot} size="small" sx={{ ml: 0.5 }}>
        <TrashSimple size={24} color="#667085" />
      </IconButton>
    </Box>
  );
}

interface EditScheduleScreenLoadedProps {
  clinicianId: string;
  weeklyData: Api.ClinicianWeeklyAvailability.V[];
  dateData: Api.ClinicianDateAvailability.V[];
  durationMinutes: number;
  bufferMinutes: number;
}

function EditScheduleScreenLoaded({
  clinicianId,
  weeklyData,
  dateData,
  durationMinutes,
  bufferMinutes,
}: EditScheduleScreenLoadedProps) {
  const navigate = useNavigate();
  const callApi = useApiCallable();

  // UI displays Monday first, but data maintains Sunday=0, Monday=1, etc.
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const uiIndexToWeekday = [1, 2, 3, 4, 5, 6, 0]; // Mon=1, ..., Sun=0

  // Convert API data to UI format
  const initialSchedule: DaySchedule[] = dayLabels.map((label, uiIndex) => {
    const weekday = uiIndexToWeekday[uiIndex];
    const weeklyItems = weeklyData.filter((item) => item.weekday === weekday);

    const slots = weeklyItems.flatMap((weekDay) =>
      weekDay.windows.map((window) => ({
        id: window['@id'],
        startTime: window.startTime,
        endTime: window.endTime,
      })),
    );

    return { day: label.toLowerCase(), label, slots };
  });

  // Convert date data
  const initialSpecificDates: Api.ClinicianDateAvailability.V[] = dateData.map(
    (dateItem) => ({
      ...dateItem,
      windows: dateItem.windows.map((window) => ({
        ...window,
        startTime: window.startTime,
        endTime: window.endTime,
      })),
    }),
  );

  const [schedule, setSchedule] = useState<DaySchedule[]>(initialSchedule);
  const [specificDates, setSpecificDates] =
    useState<Api.ClinicianDateAvailability.V[]>(initialSpecificDates);
  const [datePickerAnchorEl, setDatePickerAnchorEl] =
    useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);

  const addTimeSlot = (dayIndex: number) => {
    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              slots: [
                ...day.slots,
                {
                  id: Date.now().toString(),
                  startTime: '09:00',
                  endTime: '17:00',
                },
              ],
            }
          : day,
      ),
    );
  };

  const removeTimeSlot = (dayIndex: number, slotId: string) => {
    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              slots: day.slots.filter((slot) => slot.id !== slotId),
            }
          : day,
      ),
    );
  };

  const updateTimeSlot = (
    dayIndex: number,
    slotId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ) => {
    setSchedule((prev) =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              slots: day.slots.map((slot) =>
                slot.id === slotId ? { ...slot, [field]: value } : slot,
              ),
            }
          : day,
      ),
    );
  };

  const addSpecificDateSlot = (dateIndex: number) => {
    setSpecificDates((prev) =>
      prev.map((date, index) =>
        index === dateIndex
          ? {
              ...date,
              windows: [
                ...(date.windows || []),
                {
                  '@id': Date.now().toString(),
                  '@type': 'TimeWindowDto',
                  startTime: '09:00',
                  endTime: '17:00',
                },
              ],
            }
          : date,
      ),
    );
  };

  const removeSpecificDateSlot = (dateIndex: number, slotId: string) => {
    setSpecificDates((prev) =>
      prev.map((date, index) =>
        index === dateIndex
          ? {
              ...date,
              windows: (date.windows || []).filter(
                (window) => window['@id'] !== slotId,
              ),
            }
          : date,
      ),
    );
  };

  const updateSpecificDateSlot = (
    dateIndex: number,
    slotId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ) => {
    setSpecificDates((prev) =>
      prev.map((date, index) =>
        index === dateIndex
          ? {
              ...date,
              windows: (date.windows || []).map((window) =>
                window['@id'] === slotId
                  ? { ...window, [field]: value }
                  : window,
              ),
            }
          : date,
      ),
    );
  };

  const addNewSpecificDate = (selectedDate: Date | null) => {
    if (!selectedDate) return;

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    const newDate: Api.ClinicianDateAvailability.V = {
      '@id': Date.now().toString(),
      '@type': 'ClinicianDateAvailability',
      date: formattedDate,
      windows: [],
    };
    setSpecificDates((prev) => [...prev, newDate]);
    setDatePickerAnchorEl(null);
  };

  const updateSpecificDate = (dateIndex: number, newDate: string) => {
    setSpecificDates((prev) =>
      prev.map((dateSchedule, index) =>
        index === dateIndex ? { ...dateSchedule, date: newDate } : dateSchedule,
      ),
    );
  };

  const removeSpecificDate = (dateIndex: number) => {
    setSpecificDates((prev) => prev.filter((_, i) => i !== dateIndex));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const dayKeyToWeekday: { [key: string]: number } = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6,
      };

      const weeklyPayload: Api.ClinicianWeeklyAvailability.W = {
        days: schedule.map((day) => ({
          weekday: dayKeyToWeekday[day.day],
          windows: day.slots.map((slot) => ({
            startTime: slot.startTime,
            endTime: slot.endTime,
          })),
        })),
      };

      const weeklyResponse = await callApi(
        `/clinicians/${clinicianId}/weekly-availabilities`,
        { method: 'POST', jsonBody: weeklyPayload },
        { errorHandler: toastErrorHandler },
      );
      if (!weeklyResponse) return;

      const datePayload =
        Model.ClinicianDateAvailability.groupWindowsByDate(specificDates);

      const dateResponse = await callApi(
        `/clinicians/${clinicianId}/date-availabilities`,
        { method: 'POST', jsonBody: datePayload },
        { errorHandler: toastErrorHandler },
      );
      if (!dateResponse) return;

      toast.success('Schedule saved successfully!');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={3} alignItems="stretch">
        <HeaderBreadcrumbs
          title="Set Your Appointment Schedule"
          items={[{ name: 'Appointments', url: '/appointments' }]}
        />

        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Appointment Duration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Appointments are always {durationMinutes} minutes long, with a{' '}
              {bufferMinutes}-minute buffer between each session.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Regular Availability
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set when you're normally available for appointments. This will be
              repeated weekly.
            </Typography>

            <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
              {schedule.map((day, dayIndex) => (
                <Card key={day.day} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, minWidth: 60 }}
                    >
                      {day.label}
                    </Typography>

                    {day.slots.length === 0 ? (
                      <Typography color="text.secondary" sx={{ minWidth: 275 }}>
                        Unavailable
                      </Typography>
                    ) : (
                      <Stack spacing={1} sx={{ minWidth: 275 }}>
                        {day.slots.map((slot) => (
                          <TimeSlotBlock
                            key={slot.id}
                            slot={slot}
                            onUpdateSlot={(field, value) =>
                              updateTimeSlot(dayIndex, slot.id, field, value)
                            }
                            onRemoveSlot={() =>
                              removeTimeSlot(dayIndex, slot.id)
                            }
                          />
                        ))}
                      </Stack>
                    )}

                    <IconButton
                      onClick={() => addTimeSlot(dayIndex)}
                      size="small"
                    >
                      <PlusCircle size={24} color="#667085" />
                    </IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Availability on specific dates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If your availability differs from your regular schedule on certain
              dates, you can indicate this here.
            </Typography>

            <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
              {specificDates.map((date, dateIndex) => (
                <Card
                  key={date['@id'] || `${date.date}-${dateIndex}`}
                  sx={{ p: 2 }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ sm: 'center' }}
                  >
                    <IconButton
                      aria-label="Remove date"
                      onClick={() => removeSpecificDate(dateIndex)}
                      size="small"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      <X size={20} color="#667085" />
                    </IconButton>
                    <TextField
                      type="date"
                      size="small"
                      value={date.date || ''}
                      onChange={(e) =>
                        updateSpecificDate(dateIndex, e.target.value)
                      }
                      sx={{ minWidth: 200 }}
                    />

                    <Stack spacing={1} sx={{ minWidth: 275 }}>
                      {(date.windows || []).length === 0 ? (
                        <Typography color="text.secondary">
                          Unavailable
                        </Typography>
                      ) : (
                        (date.windows || []).map((w) => (
                          <TimeSlotBlock
                            key={w['@id']}
                            slot={{
                              id: w['@id'],
                              startTime: w.startTime,
                              endTime: w.endTime,
                            }}
                            onUpdateSlot={(field, value) =>
                              updateSpecificDateSlot(
                                dateIndex,
                                w['@id'],
                                field,
                                value,
                              )
                            }
                            onRemoveSlot={() =>
                              removeSpecificDateSlot(dateIndex, w['@id'])
                            }
                          />
                        ))
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <IconButton
                        onClick={() => addSpecificDateSlot(dateIndex)}
                        size="small"
                      >
                        <PlusCircle size={24} color="#667085" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}

              <Button
                startIcon={<Plus size={16} />}
                variant="outlined"
                color="primary"
                onClick={(e) => setDatePickerAnchorEl(e.currentTarget)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Date
              </Button>

              <Popover
                open={Boolean(datePickerAnchorEl)}
                anchorEl={datePickerAnchorEl}
                onClose={() => setDatePickerAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateCalendar
                    minDate={new Date()}
                    onChange={addNewSpecificDate}
                    sx={{ m: 0 }}
                  />
                </LocalizationProvider>
              </Popover>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Schedule'}
            </Button>
          </Box>
        </Stack>
      </Stack>
    </>
  );
}

interface EditScheduleViewProps {
  clinicianId: string;
  durationMinutes: number;
  bufferMinutes: number;
}

export default function EditScheduleView({
  clinicianId,
  durationMinutes,
  bufferMinutes,
}: EditScheduleViewProps) {
  const weeklyAvailabilityApi =
    Api.ClinicianWeeklyAvailability.useReadCollectionWeeklyAvailabilities(
      clinicianId,
    );

  const dateAvailabilityApi =
    Api.ClinicianDateAvailability.useReadCollectionDateAvailabilities(
      clinicianId,
    );

  if (weeklyAvailabilityApi.error)
    return <ScreenError {...weeklyAvailabilityApi} />;
  if (dateAvailabilityApi.error)
    return <ScreenError {...dateAvailabilityApi} />;

  if (!weeklyAvailabilityApi.data || !dateAvailabilityApi.data)
    return <SoloLoading />;

  return (
    <ScreenWrap>
      <EditScheduleScreenLoaded
        clinicianId={clinicianId}
        weeklyData={weeklyAvailabilityApi.data.member}
        dateData={dateAvailabilityApi.data.member}
        durationMinutes={durationMinutes}
        bufferMinutes={bufferMinutes}
      />
    </ScreenWrap>
  );
}
